import { Component, computed, inject, input, model, ChangeDetectionStrategy } from '@angular/core';
import { HisUiService } from '../../../core/service/base/his-ui.service';

/** Einzelne Button-Komponente der gesamten Helptext-Komponente.
 * Der Button zeigt je nach Zustand unterschiedliche Titel und Linktexte an, die über die Inputs gesteuert werden.
 * Kann für eine aufgeteilte Nutzung von Helptext-Button und Helptext-Content verwendet werden, siehe Beispiel in der HisPanelHelptextDirective.
 */
@Component( {
    selector: 'his-helptext-button',
    templateUrl: './his-helptext-button.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisHelptextButtonComponent {
    private readonly hisUi = inject( HisUiService );
    
    /** Hover-Titel des Buttons im geschlossenen Zustand. */
    readonly title = input<string>( this.hisUi.translate( 'common.help' ) );
    /** Sichtbarer Text neben dem Icon im geschlossenen Zustand. Wird kein Wert übergeben, erscheint kein Text. */
    readonly linkTitle = input<string>();
    /** Hover-Titel des Buttons im geöffneten Zustand. Fällt auf {@link HisHelptextButtonComponent.title} zurück, wenn nicht gesetzt. */
    readonly closingTitle = input<string>();
    /** Sichtbarer Text neben dem Icon im geöffneten Zustand. Fällt auf {@link HisHelptextButtonComponent.linkTitle} zurück, wenn nicht gesetzt. */
    readonly closingLinkTitle = input<string>();
    /** Gibt an, ob der Hilfetext angezeigt wird. */
    readonly displayHelptext = model.required<boolean>();

    /** Ergibt den Hover-Titel abhängig vom aktuellen Zustand. */
    protected readonly resolvedTitle = computed( () => {
        // read "closingTitle" before the conditional to make sure the signal is tracked for changes
        const closingTitle = this.closingTitle() ?? this.title();
        return this.displayHelptext() ? closingTitle : this.title();
    } );

    /** Ergibt den sichtbaren Text neben dem Icon abhängig vom aktuellen Zustand. */
    protected readonly resolvedLinkTitle = computed( () => {
        // read "closingLinkTitle" before the conditional to make sure the signal is tracked for changes
        const closingLinkTitle = this.closingLinkTitle() ?? this.linkTitle();
        return this.displayHelptext() ? closingLinkTitle : this.linkTitle();
    } );

    /** Wechselt den Zustand des Buttons zwischen geöffnet und geschlossen. */
    protected toggle() {
        this.displayHelptext.update( val => !val );
    }
}